/**
 * Atlas — Bookings page: full workflow with status actions and in-booking chat.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useBookings, useBookingMessages } from '../hooks/useData';
import { marketplaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Chip, Spinner, EmptyState, fmtDate, fmtCost } from '../components/common/UIComponents';

const STATUS_META = {
  REQUESTED:   { label: 'Requested',   variant: 'amber' },
  QUOTED:      { label: 'Quoted',      variant: 'forest' },
  ACCEPTED:    { label: 'Accepted',    variant: 'forest' },
  IN_PROGRESS: { label: 'In Progress', variant: 'moss' },
  COMPLETED:   { label: 'Completed',   variant: 'forest' },
  CANCELLED:   { label: 'Cancelled',   variant: 'crimson' },
};

const URGENCY_LABEL = { now: 'ASAP', week: 'This week', month: 'This month', plan: 'Planning' };

export default function BookingsPage() {
  const { bookings, loading, refetch } = useBookings();
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState(null);

  // Auto-select first booking
  useEffect(() => {
    if (bookings.length > 0 && !selectedId) {
      setSelectedId(bookings[0].id);
    }
  }, [bookings, selectedId]);

  const selected = bookings.find((b) => b.id === selectedId) || null;

  if (loading) return <Spinner />;

  return (
    <div className="master-detail">
      {/* ── Left panel: booking list ── */}
      <div className="master-panel" style={{ display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
        <div style={{ padding: '18px 18px 12px', borderBottom: '1px solid var(--hairline)' }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            {user?.role === 'pro' ? 'Incoming Jobs' : 'My Bookings'}
          </div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: 'var(--forest)', marginTop: 2 }}>
            {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {bookings.length === 0 ? (
            <EmptyState message={user?.role === 'pro' ? 'No incoming requests yet.' : 'No bookings yet. Browse the marketplace to request quotes.'} />
          ) : (
            bookings.map((b) => (
              <BookingListItem
                key={b.id}
                booking={b}
                active={b.id === selectedId}
                onClick={() => setSelectedId(b.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right panel: booking detail + chat ── */}
      <div className="detail-panel" style={{ display: 'flex', flexDirection: 'column' }}>
        {selected ? (
          <BookingDetail booking={selected} user={user} onRefresh={refetch} />
        ) : (
          <div style={{ display: 'grid', placeItems: 'center', flex: 1, color: 'var(--ink-4)', fontSize: 14 }}>
            Select a booking to view details
          </div>
        )}
      </div>
    </div>
  );
}

function BookingListItem({ booking, active, onClick }) {
  const meta = STATUS_META[booking.status] || { label: booking.status, variant: '' };
  return (
    <div
      onClick={onClick}
      style={{
        padding: '14px 18px', cursor: 'pointer',
        borderBottom: '1px solid var(--hairline)',
        background: active ? 'rgba(31,58,46,0.06)' : 'transparent',
        borderLeft: active ? '3px solid var(--forest)' : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div className="serif" style={{ fontSize: 14, fontWeight: 600, color: 'var(--forest)', lineHeight: 1.3 }}>
          {booking.trade}
        </div>
        <Chip variant={meta.variant} style={{ fontSize: 10, height: 18, padding: '0 7px', flexShrink: 0 }}>
          {meta.label}
        </Chip>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>
        {booking.property_address || 'Property'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 5, display: 'flex', gap: 10 }}>
        <span>{fmtDate(booking.created_at)}</span>
        {booking.quoted_amount && <span>{fmtCost(booking.quoted_amount)}</span>}
      </div>
    </div>
  );
}

function BookingDetail({ booking, user, onRefresh }) {
  const { messages, refetch: refetchMsgs } = useBookingMessages(booking.id);
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    const body = msgBody.trim();
    if (!body) return;
    setSending(true);
    try {
      await marketplaceAPI.sendMessage(booking.id, body);
      setMsgBody('');
      await refetchMsgs();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
  };

  const meta = STATUS_META[booking.status] || { label: booking.status, variant: '' };

  return (
    <>
      {/* Header */}
      <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--hairline)', background: 'var(--paper)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 className="serif" style={{ fontSize: 22, fontWeight: 600, color: 'var(--forest)', margin: 0 }}>
                {booking.trade}
              </h2>
              <Chip variant={meta.variant}>{meta.label}</Chip>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              {booking.pro_name && <><strong>{booking.pro_name}</strong> · </>}
              {booking.property_address || 'Property'} · Urgency: <strong>{URGENCY_LABEL[booking.urgency] || booking.urgency}</strong>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {booking.quoted_amount && (
              <div>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>Quoted</div>
                <div className="serif tnum" style={{ fontSize: 22, fontWeight: 600 }}>{fmtCost(booking.quoted_amount)}</div>
              </div>
            )}
            {booking.final_amount && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>Final</div>
                <div className="serif tnum" style={{ fontSize: 18, fontWeight: 600 }}>{fmtCost(booking.final_amount)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div style={{
          marginTop: 12, padding: '10px 14px',
          background: 'var(--bg)', borderRadius: 6,
          fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6,
        }}>
          {booking.description}
        </div>

        {/* Metadata row */}
        <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          <span>Created {fmtDate(booking.created_at)}</span>
          {booking.scheduled_date && <span>Scheduled {fmtDate(booking.scheduled_date)}</span>}
          {booking.completed_date && <span>Completed {fmtDate(booking.completed_date)}</span>}
        </div>

        {/* Invoice + payment block. Visible once the pro stamps the
            invoice (owner: read-only "you owe $X"; pro: gets Mark paid
            action). Pro on COMPLETED + no invoice yet gets the Issue
            invoice action. */}
        <InvoiceBlock booking={booking} user={user} onRefresh={onRefresh} />

        {/* Workflow actions */}
        <WorkflowActions booking={booking} user={user} onRefresh={onRefresh} />
      </div>

      {/* Chat thread */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--ink-4)', fontSize: 13 }}>
            No messages yet. Start the conversation below.
          </div>
        ) : (
          messages.map((msg) => (
            <ChatBubble key={msg.id} msg={msg} isOwn={msg.sender_id === user?.id} />
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Message input */}
      {['REQUESTED', 'QUOTED', 'ACCEPTED', 'IN_PROGRESS'].includes(booking.status) && (
        <div style={{
          padding: '12px 24px', borderTop: '1px solid var(--hairline)',
          background: 'var(--paper)', flexShrink: 0, display: 'flex', gap: 10,
        }}>
          <textarea
            value={msgBody}
            onChange={(e) => setMsgBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (⌘↵ to send)"
            rows={2}
            style={{
              flex: 1, resize: 'none', padding: '8px 12px',
              border: '1px solid var(--hairline)', borderRadius: 6,
              background: 'var(--card)', fontSize: 13, fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <button
            className="btn primary"
            onClick={handleSend}
            disabled={sending || !msgBody.trim()}
            style={{ alignSelf: 'flex-end', padding: '8px 18px' }}
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      )}
    </>
  );
}

function ChatBubble({ msg, isOwn }) {
  const roleColor = { owner: 'var(--forest)', agent: '#5d7a5e', pro: '#a87a2a' };
  const color = roleColor[msg.sender_role] || 'var(--ink-2)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', maxWidth: '75%', alignSelf: isOwn ? 'flex-end' : 'flex-start' }}>
      <div style={{ fontSize: 10, color: 'var(--ink-4)', marginBottom: 3, paddingLeft: isOwn ? 0 : 4, paddingRight: isOwn ? 4 : 0 }}>
        <span style={{ fontWeight: 600, color }}>{msg.sender_name}</span>
        {' · '}{fmtDate(msg.created_at)}
      </div>
      <div style={{
        padding: '10px 14px',
        borderRadius: isOwn ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
        background: isOwn ? 'var(--forest)' : 'var(--paper)',
        color: isOwn ? 'var(--paper)' : 'var(--ink-1)',
        fontSize: 13, lineHeight: 1.5,
        border: isOwn ? 'none' : '1px solid var(--hairline)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {msg.body}
      </div>
    </div>
  );
}

function WorkflowActions({ booking, user, onRefresh }) {
  const [quoteForm, setQuoteForm] = useState({ amount: '', date: '' });
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isPro = user?.role === 'pro';
  const s = booking.status;

  const patch = async (payload) => {
    setBusy(true);
    setError('');
    try {
      await marketplaceAPI.updateBooking(booking.id, payload);
      await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const submitQuote = async () => {
    if (!quoteForm.amount) { setError('Enter a quote amount.'); return; }
    await patch({
      status: 'QUOTED',
      quoted_amount: parseFloat(quoteForm.amount),
      scheduled_date: quoteForm.date || undefined,
    });
    setShowQuoteForm(false);
  };

  if (['COMPLETED', 'CANCELLED'].includes(s)) return null;

  return (
    <div style={{ marginTop: 14 }}>
      {error && <div style={{ fontSize: 12, color: 'var(--crimson)', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Pro actions */}
        {isPro && s === 'REQUESTED' && !showQuoteForm && (
          <button className="btn primary sm" onClick={() => setShowQuoteForm(true)}>
            Submit quote
          </button>
        )}
        {isPro && s === 'ACCEPTED' && (
          <button className="btn primary sm" disabled={busy} onClick={() => patch({ status: 'IN_PROGRESS' })}>
            {busy ? '…' : 'Start job'}
          </button>
        )}
        {isPro && s === 'IN_PROGRESS' && (
          <button className="btn primary sm" disabled={busy} onClick={() => patch({ status: 'COMPLETED' })}>
            {busy ? '…' : 'Mark complete'}
          </button>
        )}

        {/* Owner actions */}
        {!isPro && s === 'QUOTED' && (
          <>
            <button className="btn primary sm" disabled={busy} onClick={() => patch({ status: 'ACCEPTED' })}>
              {busy ? '…' : 'Accept quote'}
            </button>
            <button className="btn ghost sm" disabled={busy} onClick={() => patch({ status: 'CANCELLED' })}>
              Decline
            </button>
          </>
        )}

        {/* Cancel — owner or pro, while active */}
        {['REQUESTED', 'ACCEPTED'].includes(s) && (
          <button className="btn ghost sm" style={{ color: 'var(--crimson)', borderColor: 'var(--crimson)' }}
            disabled={busy} onClick={() => patch({ status: 'CANCELLED' })}>
            Cancel
          </button>
        )}
      </div>

      {/* Inline quote form for pro */}
      {isPro && showQuoteForm && (
        <div style={{
          marginTop: 12, padding: '14px 16px',
          background: 'var(--card)', borderRadius: 8,
          border: '1px solid var(--hairline)',
          display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end',
        }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Quote amount ($)</label>
            <input
              type="number"
              placeholder="e.g. 1200"
              value={quoteForm.amount}
              onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })}
              min={0}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Proposed date (optional)</label>
            <input
              type="date"
              value={quoteForm.date}
              onChange={(e) => setQuoteForm({ ...quoteForm, date: e.target.value })}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary sm" disabled={busy} onClick={submitQuote}>
              {busy ? '…' : 'Send'}
            </button>
            <button className="btn ghost sm" onClick={() => { setShowQuoteForm(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Invoice + payment block. Mirrors the mobile InvoicePaymentCard /
 * OwnerInvoiceCard split:
 *   - No invoice yet + pro + status COMPLETED → "Issue invoice" form
 *   - Invoice issued + not paid + pro → "Mark paid" form
 *   - Invoice issued + paid                 → paid summary (+ Reverse for pro)
 *   - Owner is always read-only (the pro mutates the state).
 */
function InvoiceBlock({ booking, user, onRefresh }) {
  const isPro = user?.role === 'pro';
  const issued = !!booking.invoice_issued_at;
  const paid   = !!booking.paid_at;
  const status = booking.status;

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPayForm,     setShowPayForm]     = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Issue-invoice form state (pro only)
  const defaultAmount = booking.final_amount ?? booking.quoted_amount ?? 0;
  const [invAmount, setInvAmount] = useState('');
  const [invDue,    setInvDue]    = useState('');

  // Mark-paid form state (pro only)
  const [payMethod, setPayMethod] = useState('');
  const [payRef,    setPayRef]    = useState('');
  const [payNotes,  setPayNotes]  = useState('');

  // Don't render the block at all unless there's invoice state to show,
  // OR the pro is on a completed job and can issue one.
  if (!issued && !(isPro && status === 'COMPLETED')) return null;

  const dueIso = booking.invoice_due_date;
  const overdue = issued && !paid && dueIso
    ? Date.parse(dueIso) < Date.now() - 24 * 60 * 60 * 1000
    : false;
  const chipVariant = paid ? 'forest' : overdue ? 'crimson' : 'amber';
  const chipLabel   = paid ? 'Paid'   : overdue ? 'Overdue' : 'Awaiting payment';

  const handleIssue = async () => {
    setBusy(true); setError('');
    try {
      const amount = invAmount === '' ? undefined : parseFloat(invAmount);
      if (amount != null && !(amount >= 0)) {
        setError('Amount must be a positive number.'); setBusy(false); return;
      }
      await marketplaceAPI.issueInvoice(booking.id, {
        amount,
        due_date: invDue || undefined,
      });
      setShowInvoiceForm(false);
      setInvAmount(''); setInvDue('');
      await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not issue invoice.');
    } finally { setBusy(false); }
  };

  const handleMarkPaid = async () => {
    setBusy(true); setError('');
    try {
      await marketplaceAPI.markPaid(booking.id, {
        method:    payMethod || undefined,
        reference: payRef.trim() || undefined,
        notes:     payNotes.trim() || undefined,
      });
      setShowPayForm(false);
      setPayMethod(''); setPayRef(''); setPayNotes('');
      await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not record payment.');
    } finally { setBusy(false); }
  };

  const handleUnmarkPaid = async () => {
    if (!window.confirm('Reverse this payment? Use only if the payment was returned.')) return;
    setBusy(true); setError('');
    try {
      await marketplaceAPI.unmarkPaid(booking.id);
      await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reverse payment.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      marginTop: 14, padding: '14px 16px',
      background: 'var(--card)', borderRadius: 8,
      border: '1px solid var(--hairline)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
          {issued ? (isPro ? 'Invoice & payment' : 'Invoice received') : 'Invoice'}
        </div>
        {issued ? <Chip variant={chipVariant}>{chipLabel}</Chip> : null}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--crimson)', marginTop: 8 }}>{error}</div>
      )}

      {issued ? (
        <div style={{ marginTop: 8 }}>
          <div className="serif tnum" style={{ fontSize: 22, fontWeight: 600 }}>
            {fmtCost(booking.invoice_amount ?? booking.final_amount ?? booking.quoted_amount)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Issued {fmtDate(booking.invoice_issued_at)}
            {dueIso ? ` · due ${fmtDate(dueIso)}` : ''}
          </div>
          {paid ? (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
              Paid {fmtDate(booking.paid_at)}
              {booking.payment_method ? ` · ${booking.payment_method.replace('_', ' ')}` : ''}
              {booking.payment_reference ? ` · ref ${booking.payment_reference}` : ''}
            </div>
          ) : null}
          {booking.payment_notes ? (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, fontStyle: 'italic' }}>
              {booking.payment_notes}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          No invoice issued yet. Defaults to {fmtCost(defaultAmount)}.
        </div>
      )}

      {/* Pro actions */}
      {isPro ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {!issued && status === 'COMPLETED' && !showInvoiceForm ? (
            <button
              className="btn primary sm"
              onClick={() => { setInvAmount(defaultAmount ? String(defaultAmount) : ''); setShowInvoiceForm(true); }}
            >Issue invoice</button>
          ) : null}
          {issued && !paid && !showPayForm ? (
            <button className="btn primary sm" onClick={() => setShowPayForm(true)}>Mark paid</button>
          ) : null}
          {paid ? (
            <button className="btn ghost sm" disabled={busy} onClick={handleUnmarkPaid}>
              {busy ? '…' : 'Reverse payment'}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Issue invoice form (pro only) */}
      {showInvoiceForm ? (
        <div style={{
          marginTop: 12, padding: '12px 14px',
          background: 'var(--bg)', borderRadius: 6,
          display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end',
        }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Amount ($)</label>
            <input
              type="number" min={0}
              value={invAmount}
              placeholder={String(defaultAmount || '0')}
              onChange={(e) => setInvAmount(e.target.value)}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Due date</label>
            <input type="date" value={invDue} onChange={(e) => setInvDue(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary sm" disabled={busy} onClick={handleIssue}>
              {busy ? '…' : 'Send'}
            </button>
            <button className="btn ghost sm" onClick={() => { setShowInvoiceForm(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Mark paid form (pro only) */}
      {showPayForm ? (
        <div style={{
          marginTop: 12, padding: '12px 14px',
          background: 'var(--bg)', borderRadius: 6,
          display: 'grid', gap: 10,
        }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Payment method</label>
            <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <option value="">—</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Reference (optional)</label>
            <input
              type="text" maxLength={100}
              value={payRef}
              placeholder="EFT ref / receipt no."
              onChange={(e) => setPayRef(e.target.value)}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Notes (optional)</label>
            <textarea
              rows={2} maxLength={500}
              value={payNotes}
              placeholder="Anything worth remembering"
              onChange={(e) => setPayNotes(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary sm" disabled={busy} onClick={handleMarkPaid}>
              {busy ? '…' : 'Confirm paid'}
            </button>
            <button className="btn ghost sm" onClick={() => { setShowPayForm(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
